import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  Pagination,
  IconButton,
  InputAdornment,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { 
  Search, 
  Clear, 
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Restore as RestoreIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { GET_PROPERTIES_WITH_RESERVATIONS, RESTORE_PROPERTY, GET_CANCELLED_JOBS_COUNT } from '../graphql/queries';
import AreaFilter from '../components/schedule/filters/AreaFilter';
import { useAuth } from '../contexts/AuthContext';

const ITEMS_PER_PAGE = 40;

const ArchivedProperties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  // Mutation for restoring properties
  const [restoreProperty] = useMutation(RESTORE_PROPERTY);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [propertyToRestore, setPropertyToRestore] = useState(null);
  
  // Query to get cancelled jobs count for information display
  const { data: cancelledJobsData, loading: cancelledJobsLoading } = useQuery(GET_CANCELLED_JOBS_COUNT, {
    variables: { 
      propertyId: propertyToRestore?.id,
      sinceDate: new Date().toISOString().split('T')[0] // Today's date
    },
    skip: !propertyToRestore,
    fetchPolicy: 'network-only'
  });

  // Initialize with user's areas if none are selected
  React.useEffect(() => {
    if (authLoading) {
      return;
    }
    
    if (!hasInitialized) {
      if (user?.areas && Array.isArray(user.areas) && user.areas.length > 0) {
        if (selectedAreas.length === 0 && !location.state?.returnToAreas) {
          console.log('ArchivedProperties: Auto-initializing with user assigned areas:', user.areas);
          setSelectedAreas(user.areas);
        }
      }
      setHasInitialized(true);
    }
  }, [user, authLoading, hasInitialized, selectedAreas.length, location.state?.returnToAreas]);

  // Set accordion expanded state based on screen size
  React.useEffect(() => {
    setAccordionExpanded(!isMobile);
  }, [isMobile]);

  // Restore pagination state when returning from edit page
  React.useEffect(() => {
    if (location.state?.returnToPage || location.state?.returnToSearch || location.state?.returnToAreas) {
      if (location.state.returnToPage) {
        setCurrentPage(location.state.returnToPage);
      }
      if (location.state.returnToSearch) {
        setSearch(location.state.returnToSearch);
      }
      if (location.state.returnToAreas) {
        setSelectedAreas(location.state.returnToAreas);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const { loading, error, data, networkStatus, refetch } = useQuery(GET_PROPERTIES_WITH_RESERVATIONS, {
    variables: {
      input: {
        limit: ITEMS_PER_PAGE || 10,
        page: currentPage || 1,
        search: search || undefined,
        areas: selectedAreas.length > 0 ? selectedAreas : undefined,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        includeArchived: true // Always include archived for this page
      }
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  // Filter to only show archived properties
  const allProperties = data?.propertiesWithReservations?.properties || [];
  const properties = allProperties.filter(property => !property.isActive);
  const totalItems = properties.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  
  // Debug logging
  console.log('ArchivedProperties Debug:', {
    userRole: user?.role,
    allPropertiesCount: allProperties.length,
    archivedPropertiesCount: properties.length,
    allProperties: allProperties.map(p => ({ id: p.id, name: p.name, isActive: p.isActive })),
    queryVariables: {
      includeArchived: true,
      limit: ITEMS_PER_PAGE,
      page: currentPage,
      search: search || undefined,
      areas: selectedAreas.length > 0 ? selectedAreas : undefined,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    }
  });

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handleAreasChange = (areas) => {
    setSelectedAreas(areas);
    setCurrentPage(1);
  };

  const handleRestoreProperty = (property) => {
    setPropertyToRestore(property);
    setRestoreDialogOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!propertyToRestore) return;
    
    try {
      await restoreProperty({
        variables: { id: propertyToRestore.id },
        refetchQueries: [{ query: GET_PROPERTIES_WITH_RESERVATIONS, variables: { input: {
          limit: ITEMS_PER_PAGE,
          page: currentPage,
          search: search || undefined,
          areas: selectedAreas.length > 0 ? selectedAreas : undefined,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          includeArchived: true
        }}}]
      });
      setRestoreDialogOpen(false);
      setPropertyToRestore(null);
    } catch (error) {
      console.error('Error restoring property:', error);
    }
  };

  const handleCancelRestore = () => {
    setRestoreDialogOpen(false);
    setPropertyToRestore(null);
  };

  const handleBackToProperties = () => {
    navigate('/dashboard/properties');
  };

  if (loading && networkStatus === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>Error loading archived properties</Typography>
          <Typography>{error.message}</Typography>
          {error.graphQLErrors?.map((err, index) => (
            <Typography key={index} color="error">GraphQL Error: {err.message}</Typography>
          ))}
          {error.networkError && (
            <Typography color="error">Network Error: {error.networkError.message}</Typography>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        {/* Header */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            backgroundColor: isMobile ? 'white' : 'transparent',
            padding: isMobile ? '16px' : '0',
            margin: isMobile ? '0 -16px 24px -16px' : '0 0 24px 0',
            borderRadius: isMobile ? '8px' : '0',
            boxShadow: isMobile ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={handleBackToProperties}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              size="small"
            >
              Back to Properties
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
              Archived Properties {loading && networkStatus !== 1 && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Typography>
          </Box>
        </div>

        {/* Search and Filter Section */}
        <Box mb={3}>
          {isMobile ? (
            <Accordion 
              expanded={accordionExpanded} 
              onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
              elevation={0}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': {
                  backgroundColor: 'grey.50',
                  borderRadius: accordionExpanded ? '8px 8px 0 0' : '8px',
                  minHeight: 56,
                  '&.Mui-expanded': {
                    minHeight: 56,
                  },
                },
                '& .MuiAccordionDetails-root': {
                  backgroundColor: 'white',
                  borderRadius: '0 0 8px 8px',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="search-filter-content"
                id="search-filter-header"
              >
                <Box display="flex" alignItems="center">
                  <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Search & Filter
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12}>
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          mb: 1, 
                          fontWeight: 600,
                          color: 'text.primary'
                        }}
                      >
                        Search Properties
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, address, or city..."
                        value={search}
                        onChange={handleSearchChange}
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: 2,
                              },
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                          endAdornment: search && (
                            <InputAdornment position="end">
                              <IconButton 
                                onClick={handleClearSearch} 
                                size="small"
                                sx={{ 
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'text.primary',
                                    backgroundColor: 'action.hover'
                                  }
                                }}
                              >
                                <Clear />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <AreaFilter
                        selectedAreas={selectedAreas}
                        onChange={handleAreasChange}
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Filter Status */}
                {search && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Active filters:
                      </Typography>
                      
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 2,
                          py: 0.5,
                          backgroundColor: 'primary.50',
                          color: 'primary.700',
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        Search: "{search}"
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ 
                            ml: 1, 
                            p: 0.25,
                            color: 'primary.600',
                            '&:hover': { 
                              backgroundColor: 'primary.100' 
                            }
                          }}
                        >
                          <Clear sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                backgroundColor: 'grey.50'
              }}
            >
              <Grid container spacing={3} alignItems="flex-end">
                <Grid item xs={12} md={8}>
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        mb: 1, 
                        fontWeight: 600,
                        color: 'text.primary'
                      }}
                    >
                      Search Properties
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search by name, address, or city..."
                      value={search}
                      onChange={handleSearchChange}
                      size="medium"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: search && (
                          <InputAdornment position="end">
                            <IconButton 
                              onClick={handleClearSearch} 
                              size="small"
                              sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'text.primary',
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <AreaFilter
                      selectedAreas={selectedAreas}
                      onChange={handleAreasChange}
                    />
                  </Box>
                </Grid>
              </Grid>
              
              {/* Filter Status */}
              {search && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Active filters:
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 2,
                        py: 0.5,
                        backgroundColor: 'primary.50',
                        color: 'primary.700',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      Search: "{search}"
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ 
                          ml: 1, 
                          p: 0.25,
                          color: 'primary.600',
                          '&:hover': { 
                            backgroundColor: 'primary.100' 
                          }
                        }}
                      >
                        <Clear sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {properties.length === 0 && !loading ? (
          <Alert severity="info">
            {search ? 'No archived properties found matching your search.' : 'No archived properties found.'}
          </Alert>
        ) : properties.length === 0 && loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>State</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties.map((property) => (
                    <TableRow key={property.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {property.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {property.address}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {property.city}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {property.state}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            component={Link}
                            to={`/dashboard/properties/edit/${property.id}`}
                            state={{
                              returnToPage: currentPage,
                              returnToSearch: search,
                              returnToAreas: selectedAreas
                            }}
                            color="primary"
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleRestoreProperty(property)}
                            color="success"
                            size="small"
                            startIcon={<RestoreIcon />}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Restore
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              mt={3} 
              px={2}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 500,
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalItems)} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} archived properties
              </Typography>
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  '& .MuiPaginationItem-root': {
                    margin: '0 4px',
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={isMobile ? 1 : 2}
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Restore Confirmation Dialog */}
        <Dialog
          open={restoreDialogOpen}
          onClose={handleCancelRestore}
          aria-labelledby="restore-dialog-title"
          aria-describedby="restore-dialog-description"
        >
          <DialogTitle id="restore-dialog-title">
            Restore Property
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="restore-dialog-description">
              Are you sure you want to restore "{propertyToRestore?.name}" to active status?
            </DialogContentText>
            {cancelledJobsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Loading job information...</Typography>
              </Box>
            ) : cancelledJobsData?.getCancelledJobsCount > 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This property has {cancelledJobsData.getCancelledJobsCount} cancelled job(s) from when it was archived.
                  You may need to manually reschedule jobs if needed.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This property can be restored without any job conflicts.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelRestore} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmRestore} color="success" variant="contained">
              Restore Property
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ArchivedProperties;
